import React, { useCallback, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import releaseDetailsService from "../../app/data/release-details/releaseDetailsService";

const IMAGE_MAX_SIZE = 10 * 1024 * 1024;
const FONT_SIZES = ["12px", "14px", "16px", "18px", "20px", "24px", "28px", "32px"];

const Size = Quill.import("attributors/style/size");
Size.whitelist = FONT_SIZES;
Quill.register(Size, true);

const Align = Quill.import("attributors/style/align");
Quill.register(Align, true);

interface ReleaseDetailsEditorProps {
  value: string;
  onChange: (value: string) => void;
}

const ReleaseDetailsEditor: React.FC<ReleaseDetailsEditorProps> = (props) => {
  const quillRef = useRef<ReactQuill>(null);

  const editorImageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/gif, image/jpeg, image/png");
    input.click();
    input.onchange = () => {
      const file = input.files && input.files[0];
      if (!file) {
        return;
      }
      if (!/^image\//.test(file.type)) {
        toast.error("Insertion failed because the object is not an image.");
        return;
      }
      if (file.size > IMAGE_MAX_SIZE) {
        toast.error("Image size must be 10 MB or smaller.");
        return;
      }
      const editor = quillRef.current?.getEditor();
      if (!editor) {
        return;
      }
      const range = editor.getSelection(true);
      releaseDetailsService.uploadImage(file).then((uploaded) => {
        if (!uploaded?.uri) {
          toast.error("An error occurred while inserting an image.");
          return;
        }
        editor.insertEmbed(range.index, "image", uploaded.uri);
      }).catch((error: Error) => {
        toast.error(error.message || "An error occurred while inserting an image.");
      });
    };
  }, []);

  const quillModules = useMemo(() => ({
    clipboard: {
      matchVisual: false
    },
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }, { size: [false, ...FONT_SIZES] }],
        [{ color: [] }],
        ["bold", "italic", "underline", "strike"],
        [{ align: ["", "center", "right", "justify"] }],
        [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
        ["blockquote", "link", "image"],
        ["clean"]
      ],
      handlers: {
        image: editorImageHandler
      }
    }
  }), [editorImageHandler]);

  return (
    <ReactQuill
      ref={quillRef}
      theme="snow"
      modules={quillModules}
      value={props.value}
      onChange={props.onChange}
      bounds=".xgs-release-details-editor"
    />
  );
};

export default ReleaseDetailsEditor;
